import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Heart, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const REACTIONS = [
  { key: "love", icon: Heart, label: "Love", color: "#FF5A79" },
  { key: "like", icon: ThumbsUp, label: "Like", color: "#2AB7CA" },
  { key: "dislike", icon: ThumbsDown, label: "Meh", color: "#94A3B8" },
];

export default function VideoModal({ video, onClose, onEnded }) {
  const { activeKid, isKid, user } = useAuth();
  const authorName = isKid ? activeKid?.name : user?.name || "Parent";
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    if (video) api.get(`/videos/${video.video_id}/interactions`).then(({ data }) => setData(data));
  }, [video]);

  useEffect(() => { setData(null); load(); }, [load]);

  const react = async (reaction) => {
    const { data } = await api.post(`/videos/${video.video_id}/react`, { reaction });
    setData((d) => ({ ...d, counts: data.counts, my_reaction: data.my_reaction }));
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    const { data: c } = await api.post(`/videos/${video.video_id}/comment`, { text: comment, author_name: authorName });
    setData((d) => ({ ...d, comments: [...(d?.comments || []), c] }));
    setComment("");
  };

  return (
    <AnimatePresence>
      {video && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={onClose} data-testid="video-modal">
          <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
            className="relative w-full max-w-3xl bg-white rounded-3xl overflow-hidden shadow-2xl my-8"
            onClick={(e) => e.stopPropagation()}>
            <button onClick={onClose} data-testid="close-video-btn"
              className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
              <X className="h-5 w-5" />
            </button>
            <video key={video.video_url} controls autoPlay className="w-full aspect-video bg-black" poster={video.thumbnail}
              onEnded={() => onEnded && onEnded(video)}>
              <source src={video.video_url} type="video/mp4" />
            </video>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="rounded-full bg-rose-100 text-rose-600 px-3 py-1 text-xs font-bold">{video.category}</span>
                <span className="rounded-full bg-sky-100 text-sky-600 px-3 py-1 text-xs font-bold">Ages {video.age_group}</span>
                <span className="text-xs text-slate-400 ml-auto">{video.duration}</span>
              </div>
              <h3 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2">
                <Play className="h-5 w-5 text-[#FF5A79]" /> {video.title}
              </h3>
              <p className="text-slate-500 mt-2">{video.description}</p>

              {/* Reactions */}
              <div className="flex items-center gap-3 mt-5" data-testid="video-reactions">
                {REACTIONS.map(({ key, icon: Icon, label, color }) => {
                  const active = data?.my_reaction === key;
                  return (
                    <button key={key} data-testid={`react-${key}`} onClick={() => react(key)}
                      className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold border-2 transition-all ${active ? "text-white" : "bg-white text-slate-500 hover:border-slate-300"}`}
                      style={active ? { backgroundColor: color, borderColor: color } : { borderColor: "#e2e8f0" }}>
                      <Icon className={`h-4 w-4 ${active && key === "love" ? "fill-white" : ""}`} />
                      {label} <span className="opacity-80">{data?.counts?.[key] ?? 0}</span>
                    </button>
                  );
                })}
              </div>

              {/* Comments — hidden for kid profiles */}
              {!isKid && (
              <div className="mt-6">
                <h4 className="font-display font-bold text-slate-800 mb-3">Comments</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto mb-3" data-testid="video-comments">
                  {(data?.comments || []).length === 0 && <p className="text-sm text-slate-400">Be the first to comment!</p>}
                  {(data?.comments || []).map((c) => (
                    <div key={c.id} className="flex gap-2 text-sm bg-slate-50 rounded-xl px-3 py-2">
                      <span className="font-bold text-slate-700">{c.name}</span>
                      <span className="text-slate-600">{c.text}</span>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendComment} className="flex gap-2">
                  <Input data-testid="video-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder={`Comment as ${authorName}…`} className="rounded-full h-10" />
                  <Button type="submit" size="icon" data-testid="video-comment-submit" className="rounded-full h-10 w-10 bg-[#FF5A79] hover:bg-[#e04865] shrink-0">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
